import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Modal } from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';

const FeedbackItem = ({ feedback, isCompact = false }) => {
    const getNPSCategory = (score) => {
        const npsScore = parseInt(score);
        if (npsScore >= 0 && npsScore <= 6) return { text: 'DETRACTOR', class: 'danger' };
        if (npsScore === 7 || npsScore === 8) return { text: 'PASIVO', class: 'warning' };
        if (npsScore >= 9 && npsScore <= 10) return { text: 'PROMOTOR', class: 'success' };
        return { text: 'N/A', class: 'secondary' };
    };

    const renderStars = (satisfaction) => {
        const stars = [];
        const score = parseInt(satisfaction);
        
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <FontAwesomeIcon
                    key={i}
                    icon="star"
                    className={`${i <= score ? 'text-warning' : 'text-300'} me-1`}
                    transform={isCompact ? 'shrink-2' : ''}
                />
            );
        }
        return stars;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const npsCategory = getNPSCategory(feedback.NetPromotionScore);

    return (
        <Card className={`mb-3 ${isCompact ? 'shadow-none' : 'shadow-sm'}`}>
            <Card.Body className={isCompact ? 'p-3' : ''}>
                <Row className="g-3 align-items-center">
					{/* Fecha */}
                    <Col xs={12}>
                        <div className="d-flex align-items-center mb-2 text-600">
                            <FontAwesomeIcon icon="calendar" className="text-primary me-2" />
                            {formatDate(feedback.Timestamp)}
                        </div>
                    </Col>

                    <Col xs={12} md={6}>
                        <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon="envelope" className="text-primary me-2" />
                            <a
                            href={`mailto:${feedback.EmailAddress}`}
                            className="text-decoration-none text-truncate"
                            >
                            {feedback.EmailAddress}
                            </a>
                        </div>
                    </Col>

                    <Col xs={12} md={6}>
                        <div className="d-flex align-items-center justify-content-md-start mb-2">
                            <div className="d-flex align-items-center">
                                <span className="me-2">Satisfacción:</span>
                                {renderStars(feedback.Satisfaction)}
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="me-2">NPS:</span>
                                <span className={`badge badge-soft-${npsCategory.class} fw-medium`}>
                                    {npsCategory.text} ({feedback.NetPromotionScore})
                                </span>
                            </div>
                        </div>
                    </Col>
                    
                    {/* Método de contacto y comentarios */}
                    <Col xs={12}>
                        <div className={`${isCompact ? 'border-top pt-2' : 'border-top pt-3'}`}>
                            {/* Método de contacto */}
                            {feedback.ContactWays && (
                                <div className={`d-flex align-items-center ${isCompact ? 'mb-2' : 'mb-2'}`}>
                                    <FontAwesomeIcon icon="phone" className="text-primary me-2" />
                                    <span className="text-600 me-2" style={{ fontSize: isCompact ? '0.85rem' : '1rem' }}>
                                        Contacto:
                                    </span>
                                    <span className="text-primary" style={{ fontSize: isCompact ? '0.85rem' : '1rem' }}>
                                        {feedback.ContactWays}
                                    </span>
                                </div>
                            )}

                            {/* Comentarios */}
                            {feedback.Comments && (
                                <div className={isCompact ? '' : 'bg-light rounded p-3'}>
                                    <div className="d-flex align-items-start">
                                        <FontAwesomeIcon 
                                            icon="comment-dots" 
                                            className="text-primary me-2 mt-1" 
                                            style={{ fontSize: isCompact ? '0.85rem' : '1rem' }}
                                        />
                                        <div className="flex-grow-1">
                                            <strong 
                                                className="d-block mb-1 text-600" 
                                                style={{ fontSize: isCompact ? '0.85rem' : '1rem' }}
                                            >
                                                Comentarios:
                                            </strong>
                                            <p 
                                                className="mb-0 text-wrap" 
                                                style={{ 
                                                    fontSize: isCompact ? '0.85rem' : '1rem',
                                                    maxHeight: isCompact ? '3em' : 'none',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: isCompact ? '-webkit-box' : 'block',
                                                    WebkitLineClamp: isCompact ? 2 : 'unset',
                                                    WebkitBoxOrient: 'vertical'
                                                }}
                                            >
                                                {feedback.Comments}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

const FeedbackList = ({ feedbacks, hasMore, loadMore }) => {
    return (
        <div className="px-3">
            <InfiniteScroll
                dataLength={feedbacks.length}
                next={loadMore}
                hasMore={hasMore}
                loader={<LoadingSkeleton />}
                height={500}
                className="pe-2"
                endMessage={
                    <p className="text-center text-muted py-3">
                        <small>No hay más retroalimentaciones</small>
                    </p>
                }
            >
                {feedbacks.map((feedback, index) => (
                    <FeedbackItem key={`${feedback.Timestamp}-${index}`} feedback={feedback} />
                ))}
            </InfiniteScroll>
        </div>
    );
};

const LoadingSkeleton = () => (
    <Card className="mb-3">
        <Card.Body>
            <Skeleton height={24} className="mb-2" />
            <Skeleton height={24} className="mb-2" />
            <Skeleton height={48} />
        </Card.Body>
    </Card>
);

const CustomerFeedbackList = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const { setToken, token } = useToken();
    const ITEMS_PER_PAGE = 10;
    const { showErrorToast } = useToast();
    const pageRef = useRef(0);

    const fetchFeedbacks = async () => {
        if (cargando) return;
        
        setCargando(true);

        try {
            const response = await fetch(`/api/soportetecnico/ObtenerCustomerFeedbacks/?page=${pageRef.current}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data && Array.isArray(data)) {
                    const newFeedbacks = data;

                    // Evitar añadir duplicados: comprobar si los nuevos ya existen
                    setFeedbacks(prev => {
                        const existingKeys = new Set(prev.map(f => `${f.Timestamp}|${f.EmailAddress}`));
                        const uniqueNew = newFeedbacks.filter(f => !existingKeys.has(`${f.Timestamp}|${f.EmailAddress}`));

                        if (uniqueNew.length > 0) {
                            pageRef.current += 1;
                        }

                        // Si no hay items nuevos, marcar que ya no hay más
                        const computedHasMore = uniqueNew.length === ITEMS_PER_PAGE && newFeedbacks.length === ITEMS_PER_PAGE;
                        setHasMore(computedHasMore);

                        if (uniqueNew.length > 0) {
                            return [...prev, ...uniqueNew];
                        }
                        return prev;
                    });
                } else {
                    setHasMore(false);
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
                setHasMore(false);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            console.error('Error al cargar feedbacks:', error);
            showErrorToast('Error al cargar los feedbacks: ' + error.message);
            setHasMore(false);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        fetchFeedbacks();
    }, []);

    return (
        <>
            <Card className="h-100">
                <FalconCardHeader
                    title="&Uacute;ltima Retroalimentaci&oacute;n del Cliente"
                    titleTag="h5"
                    className="py-2"
                    endEl={
                        <Button
                            variant="falcon-default"
                            size="sm"
                            onClick={() => setShowModal(true)}
                            disabled={feedbacks.length === 0}
                        >
                            <FontAwesomeIcon icon="eye" className="me-1" /> Ver m&aacute;s retroalimentaci&oacute;n
                        </Button>
                    }
                />
                <Card.Body className="bg-light p-0">
                    {cargando && feedbacks.length === 0 ? (
                        <div className="p-3">
                            <LoadingSkeleton />
                        </div>
                    ) : feedbacks.length > 0 ? (
                        // ✨ SOLUCIÓN: Agregar ID al contenedor scrollable
                        <div 
                            id="scrollableFeedbackDiv" 
                            style={{ height: '400px', overflowY: 'auto' }}
                        >
                            <InfiniteScroll
                                dataLength={feedbacks.length}
                                next={fetchFeedbacks}
                                hasMore={hasMore}
                                loader={
                                    <div className="p-3 text-center">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                        <p className="text-muted mt-2 mb-0">
                                            <small>Cargando más retroalimentaciones...</small>
                                        </p>
                                    </div>
                                }
                                endMessage={
                                    <p className="text-center text-muted py-3 mb-0">
                                        <small>✓ No hay más retroalimentaciones.</small>
                                    </p>
                                }
                                scrollableTarget="scrollableFeedbackDiv" // ✨ CRÍTICO: Vincular con el ID
                            >
                                <div className="p-3">
                                    {feedbacks.map((feedback, index) => (
                                        <FeedbackItem 
                                            key={`${feedback.Timestamp}-${index}`} 
                                            feedback={feedback} 
                                            isCompact={true} 
                                        />
                                    ))}
                                </div>
                            </InfiniteScroll>
                        </div>
                    ) : (
                        <div className="p-3">
                            <p className="text-center mb-0 text-muted">No hay retroalimentaci&oacute;n disponible</p>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                centered
                dialogClassName="modal-90w"
            >
                <Modal.Header closeButton className="border-200">
                    <Modal.Title className="fs-4">
                        <FontAwesomeIcon icon="comments" className="text-primary me-2" />
                        Retroalimentaci&oacute;n de Clientes
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 bg-light">
                    <FeedbackList
                        feedbacks={feedbacks}
                        hasMore={hasMore}
                        loadMore={fetchFeedbacks}
                    />
                </Modal.Body>
                <Modal.Footer className="border-200">
                    <Button variant="falcon-default" onClick={() => setShowModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

CustomerFeedbackList.propTypes = {
};

FeedbackItem.propTypes = {
    feedback: PropTypes.shape({
        EmailAddress: PropTypes.string.isRequired,
        Satisfaction: PropTypes.string.isRequired,
        NetPromotionScore: PropTypes.string.isRequired,
        ContactWays: PropTypes.string,
        Comments: PropTypes.string,
        Timestamp: PropTypes.string.isRequired
    }).isRequired,
    isCompact: PropTypes.bool
};

FeedbackList.propTypes = {
    feedbacks: PropTypes.array.isRequired,
    hasMore: PropTypes.bool.isRequired,
    loadMore: PropTypes.func.isRequired
};

export default CustomerFeedbackList;